import { Injectable } from '@angular/core';
import { ThemeService } from './theme.service';

@Injectable({
  providedIn: 'root'
})
export class ThemeTestService {

  constructor(private themeService: ThemeService) {}

  /**
   * Test theme application with Stencil components
   */
  async testStencilComponentTheming(): Promise<boolean> {
    try {
      // Wait for components to be defined
      await this.waitForStencilComponents();

      // Get current theme properties
      const customProperties = this.themeService.getCustomProperties();

      // Test Stencil component integration
      const testResults = {
        customPropertiesExtracted: Object.keys(customProperties).length > 0,
        stencilComponentsFound: this.findStencilComponents().length > 0,
        propertiesApplied: this.verifyPropertiesApplied(customProperties),
        themeStateValid: this.verifyThemeState()
      };

      console.log('Stencil Component Theme Test Results:', testResults);

      // Return overall success
      return Object.values(testResults).every(result => result === true);
    } catch (error) {
      console.error('Stencil component theme test failed:', error);
      return false;
    }
  }

  /**
   * Find all Stencil components (custom elements with hyphens)
   */
  private findStencilComponents(): HTMLElement[] {
    const allElements = document.querySelectorAll('*');
    const stencilComponents: HTMLElement[] = [];

    allElements.forEach(element => {
      if (element.tagName.includes('-') && element.tagName !== 'ROUTER-OUTLET') {
        stencilComponents.push(element as HTMLElement);
      }
    });

    return stencilComponents;
  }

  /**
   * Verify that theme properties are applied to Stencil components
   */
  private verifyPropertiesApplied(customProperties: Record<string, string>): boolean {
    const stencilComponents = this.findStencilComponents();

    if (stencilComponents.length === 0) {
      console.warn('No Stencil components found for theme testing');
      return true; // No components to test is not a failure
    }

    // Check a few key theme properties
    const keyProperties = [
      '--xp-ref-color-brand-primary',
      '--md-sys-color-primary',
      '--xp-ref-color-semantic-interaction'
    ];

    let propertiesApplied = 0;

    stencilComponents.forEach(component => {
      const computedStyle = getComputedStyle(component);

      keyProperties.forEach(property => {
        if (customProperties[property]) {
          const appliedValue = computedStyle.getPropertyValue(property);
          if (appliedValue && appliedValue.trim()) {
            propertiesApplied++;
          }
        }
      });
    });

    // Consider successful if any properties are applied
    return propertiesApplied > 0;
  }

  /**
   * Verify theme state is valid
   */
  private verifyThemeState(): boolean {
    const themeState = this.themeService.themeState$.getValue();

    // Theme state is valid if either we have a current theme or we're in a valid no-theme state
    return !!(themeState.currentTheme || (!themeState.isLoading && !themeState.error));
  }

  /**
   * Wait for Stencil components to be defined
   */
  private async waitForStencilComponents(timeout = 2000): Promise<void> {
    const start = Date.now();

    while (Date.now() - start < timeout) {
      const undefinedElements = document.querySelectorAll(':not(:defined)');
      if (undefinedElements.length === 0) {
        break;
      }

      // Wait a bit for more components to load
      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }

  /**
   * Test specific Stencil component theme integration
   */
  testSpecificComponent(componentSelector: string): {
    found: boolean;
    hasThemeProperties: boolean;
    customProperties: Record<string, string>;
  } {
    const component = document.querySelector(componentSelector) as HTMLElement;

    if (!component) {
      return {
        found: false,
        hasThemeProperties: false,
        customProperties: {}
      };
    }

    const computedStyle = getComputedStyle(component);
    const customProperties: Record<string, string> = {};

    // Extract custom properties from the component
    const allStyles = Array.from(computedStyle);
    allStyles.forEach(property => {
      if (property.startsWith('--')) {
        const value = computedStyle.getPropertyValue(property);
        if (value && value.trim()) {
          customProperties[property] = value.trim();
        }
      }
    });

    return {
      found: true,
      hasThemeProperties: Object.keys(customProperties).length > 0,
      customProperties
    };
  }

  /**
   * Generate theme test report
   */
  async generateThemeTestReport(): Promise<string> {
    const testResult = await this.testStencilComponentTheming();
    const themeState = this.themeService.themeState$.getValue();
    const stencilComponents = this.findStencilComponents();

    return `
# Theme Integration Test Report

## Test Results: ${testResult ? 'PASSED' : 'FAILED'}

### Theme State
- Current Theme: ${themeState.currentTheme || 'None'}
- Partner Theme: ${themeState.partnerTheme?.cssFileName || 'None'}
- Fallback Active: ${themeState.fallbackActive}
- Is Loading: ${themeState.isLoading}
- Error: ${themeState.error || 'None'}

### Component Analysis
- Stencil Components Found: ${stencilComponents.length}
- Component Tags: ${stencilComponents.map(c => c.tagName).join(', ')}
- Custom Properties Count: ${Object.keys(this.themeService.getCustomProperties()).length}

### Integration Status
${testResult ? '✅ Theme system successfully integrated with Stencil components' : '❌ Theme integration issues detected'}

Generated: ${new Date().toISOString()}
    `.trim();
  }
}
